import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ForwardRefExoticComponent,
  type FocusEventHandler,
  type MouseEventHandler,
  type ReactNode,
  type RefAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { ensurePortalRoot } from '../shared/index.js';
import { useFloating } from '../../hooks/useFloating.js';
import { useOutsideClick } from '../../hooks/useOutsideClick.js';
import { useEscapeStack } from '../../hooks/useEscapeStack.js';
import { ColorPicker, type ColorPickerProps } from './ColorPicker.js';

export interface ColorPickerButtonHandle {
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export interface ColorPickerButtonProps extends Omit<ColorPickerProps, 'className' | 'aria-label'> {
  /** Accessible label on the trigger — e.g. "Text colour". */
  'aria-label': string;
  /** Replace the default trigger glyph (a square swatch + tiny chevron).
   *  Receives the live colour so the icon can carry an underline tint
   *  in BubbleToolbar usage. */
  renderTrigger?: (ctx: { color: string; open: boolean }) => ReactNode;
  /** Preferred popover side. Default `'bottom-start'`. */
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'bottom' | 'top';
  /** Class name forwarded to the trigger button. */
  className?: string;
  /** Class name forwarded to the popover panel. */
  panelClassName?: string;
  /** Optional id for the trigger. */
  id?: string;
  /** Disable the trigger entirely. */
  disabled?: boolean;
  /** Trigger button size (visual only). */
  triggerSize?: 'sm' | 'md' | 'lg';
  /** Notified whenever the popover open state changes. Useful for hosts
   *  that need to lock external UI (e.g. an editor's selection bubble)
   *  while the picker is on screen. */
  onOpenChange?: (open: boolean) => void;
  /** Hover/focus event passthrough — Tooltip and other floating-UI
   *  primitives rely on these to anchor their popups. Forwarded onto
   *  the trigger button verbatim. */
  onMouseEnter?: MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: MouseEventHandler<HTMLButtonElement>;
  onFocus?: FocusEventHandler<HTMLButtonElement>;
  onBlur?: FocusEventHandler<HTMLButtonElement>;
  /** Custom mousedown handler. When omitted and `preserveFocus` is true,
   *  the trigger calls `preventDefault()` on mousedown so opening the
   *  picker does not steal focus from a contenteditable host. */
  onMouseDown?: MouseEventHandler<HTMLButtonElement>;
  /** Native tooltip / aria-describedby passthrough. */
  title?: string;
  'aria-describedby'?: string;
}

/** Click-to-open colour picker — wraps the inline `<ColorPicker>` in a
 *  portalled popover with proper boundary detection (delegated to
 *  `useFloating`). Designed to drop straight into BubbleToolbar /
 *  right-rail slots: imperative `open()` / `close()` lets a parent
 *  trigger the panel from a non-button affordance (e.g. a clicked text
 *  span in the editor). */
export const ColorPickerButton: ForwardRefExoticComponent<
  ColorPickerButtonProps & RefAttributes<ColorPickerButtonHandle>
> = forwardRef<ColorPickerButtonHandle, ColorPickerButtonProps>(
  function ColorPickerButton(props, fwdRef) {
    const {
      value,
      defaultValue,
      onChange,
      swatches,
      showHex,
      alpha,
      size,
      defaultFormat,
      preserveSwatchValue,
      resolveElement,
      preserveFocus = false,
      onReset,
      resetLabel,
      placement = 'bottom-start',
      renderTrigger,
      className,
      panelClassName,
      id,
      disabled = false,
      triggerSize = 'md',
      onOpenChange,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      onMouseDown,
      title,
      'aria-describedby': ariaDescribedBy,
      'aria-label': ariaLabel,
    } = props;

    const [open, setOpenState] = useState(false);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);

    /* Wrap the open setter so every state transition fires the
     * `onOpenChange` callback. The notification fires from a
     * `useEffect` (one tick AFTER the state update commits) — never
     * from inside the state-updater function — so consumers that
     * setState in their `onOpenChange` (e.g. an editor bubble that
     * locks itself when a picker opens) don't trip React's
     * "Cannot update a component while rendering a different
     * component" warning. */
    const onOpenChangeRef = useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;
    const lastNotifiedOpenRef = useRef(open);
    useEffect(() => {
      if (lastNotifiedOpenRef.current !== open) {
        lastNotifiedOpenRef.current = open;
        onOpenChangeRef.current?.(open);
      }
    }, [open]);
    const setOpen = useCallback((next: boolean | ((prev: boolean) => boolean)) => {
      setOpenState(next);
    }, []);

    // Mirror the value internally so the trigger swatch updates even when
    // the consumer is uncontrolled (defaultValue + onChange).
    const [internalValue, setInternalValue] = useState<string>(value ?? defaultValue ?? '#000000');
    const liveValue = value ?? internalValue;

    const handleChange = useCallback(
      (next: string) => {
        setInternalValue(next);
        onChange?.(next);
      },
      [onChange],
    );

    const { setAnchor, setFloating, rect, floatingStyle } = useFloating({
      open,
      placement,
      gap: 8,
      padding: 8,
      flip: true,
    });

    useOutsideClick(
      panelRef,
      useCallback(() => {
        setOpen(false);
      }, []),
      { enabled: open, extraRefs: [triggerRef] },
    );

    // Escape via the shared modal stack — only the topmost layer responds.
    useEscapeStack(
      useCallback(() => {
        setOpen(false);
        triggerRef.current?.focus();
      }, []),
      open,
    );

    useImperativeHandle(
      fwdRef,
      () => ({
        open: () => {
          if (!disabled) setOpen(true);
        },
        close: () => {
          setOpen(false);
        },
        toggle: () => {
          if (!disabled) setOpen((o) => !o);
        },
      }),
      [disabled],
    );

    const portalRoot = ensurePortalRoot();
    const triggerClasses = [
      'vq-color-picker-trigger',
      `vq-color-picker-trigger--${triggerSize}`,
      open && 'vq-color-picker-trigger--open',
      className,
    ]
      .filter(Boolean)
      .join(' ');
    const panelClasses = ['vq-color-picker-pop', panelClassName].filter(Boolean).join(' ');
    const pickerResolveElement = resolveElement ?? triggerRef.current;

    return (
      <>
        <button
          ref={(node) => {
            triggerRef.current = node;
            setAnchor(node);
          }}
          id={id}
          type="button"
          className={triggerClasses}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          title={title}
          data-component="ColorPickerButton"
          data-state={open ? 'open' : 'idle'}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onFocus={onFocus}
          onBlur={onBlur}
          onMouseDown={(e) => {
            // `preserveFocus` is the public contract: when true, the
            // trigger MUST NOT steal focus from whatever opened the
            // picker (typically a contenteditable host). We always
            // preventDefault first; the consumer's handler runs after
            // and is free to call `stopPropagation`, etc.
            if (preserveFocus) e.preventDefault();
            onMouseDown?.(e);
          }}
          onClick={() => {
            if (!disabled) setOpen((o) => !o);
          }}
        >
          {renderTrigger !== undefined ? (
            renderTrigger({ color: liveValue, open })
          ) : (
            <>
              <span
                className="vq-color-picker-trigger__swatch"
                aria-hidden="true"
                style={
                  {
                    '--vq-cp-trigger-color': liveValue,
                  } as React.CSSProperties
                }
              />
              <svg
                className="vq-color-picker-trigger__chevron"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                aria-hidden="true"
              >
                <polyline
                  points="2 4 5 7 8 4"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </>
          )}
        </button>
        {open && portalRoot !== null
          ? createPortal(
              <div
                ref={(node) => {
                  panelRef.current = node;
                  setFloating(node);
                }}
                className={panelClasses}
                role="dialog"
                aria-label={ariaLabel}
                data-component="ColorPickerPanel"
                data-side={rect?.side ?? 'bottom'}
                style={floatingStyle}
              >
                <ColorPicker
                  value={liveValue}
                  onChange={handleChange}
                  {...(defaultValue !== undefined ? { defaultValue } : {})}
                  {...(swatches !== undefined ? { swatches } : {})}
                  {...(showHex !== undefined ? { showHex } : {})}
                  {...(alpha !== undefined ? { alpha } : {})}
                  {...(size !== undefined ? { size } : {})}
                  {...(defaultFormat !== undefined ? { defaultFormat } : {})}
                  {...(preserveSwatchValue !== undefined ? { preserveSwatchValue } : {})}
                  {...(pickerResolveElement !== null && pickerResolveElement !== undefined
                    ? { resolveElement: pickerResolveElement }
                    : {})}
                  preserveFocus={preserveFocus}
                  {...(onReset !== undefined ? { onReset } : {})}
                  {...(resetLabel !== undefined ? { resetLabel } : {})}
                  aria-label={ariaLabel}
                />
              </div>,
              portalRoot,
            )
          : null}
      </>
    );
  },
);
